import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [isNewLogin, setIsNewLogin] = useState(false); // Para mostrar modal de push

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const savedToken = localStorage.getItem('token');
    if (savedToken) {
      try {
        const response = await authAPI.me();
        setUser(response.data);
        setToken(savedToken);
        // Mantener localStorage en sync con la última info del backend
        // (incluye taller_info con razon_social, numero_anac, ubicacion → usado
        //  por la marca de agua del visor PDF y otras vistas).
        localStorage.setItem('user', JSON.stringify(response.data));
        setMustChangePassword(response.data.must_change_password || false);
      } catch (error) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setToken(null);
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    const response = await authAPI.login(email, password);
    const { access_token, user: userData, must_change_password } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    setToken(access_token);
    setMustChangePassword(must_change_password || false);
    
    // Enriquecer inmediatamente con /auth/me para traer taller_info
    // (login response no incluye taller_info → necesario para la marca de agua del visor PDF)
    let userMeData = null;
    try {
      const meRes = await authAPI.me();
      userMeData = meRes.data;
      setUser(meRes.data);
      localStorage.setItem('user', JSON.stringify(meRes.data));
    } catch (e) {
      // silencioso — usuario sigue navegando con los datos básicos
    }
    
    // Marcar como nuevo login para mostrar modal de push
    // Respeta el flag persistente del backend `no_mostrar_modal_push` (sobrevive a cache clear)
    const pushModalShown = localStorage.getItem('push_modal_shown');
    const noMostrarBackend = !!(userMeData && userMeData.no_mostrar_modal_push);
    if (!pushModalShown && !noMostrarBackend && !must_change_password) {
      setIsNewLogin(true);
    }
    
    return { user: userData, mustChangePassword: must_change_password };
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setToken(null);
    setMustChangePassword(false);
    setIsNewLogin(false);
  };
  
  const clearNewLoginFlag = () => {
    setIsNewLogin(false);
  };

  const changePassword = async (oldPassword, newPassword) => {
    await authAPI.changePassword(oldPassword, newPassword);
    setMustChangePassword(false);
  };

  const isAdmin = () => {
    return user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'admin_finanzas';
  };

  const isSuperAdmin = () => {
    return user?.role === 'super_admin';
  };

  const isAdminFinanzas = () => {
    return user?.role === 'admin_finanzas';
  };

  const isTallerOwner = () => {
    return user?.role === 'taller_owner';
  };

  const isTallerUser = () => {
    return user?.role === 'taller_user' || user?.role === 'taller_owner' || user?.role === 'admin_taller';
  };

  const value = {
    user,
    token,
    loading,
    mustChangePassword,
    isNewLogin,
    login,
    logout,
    changePassword,
    clearNewLoginFlag,
    isAdmin,
    isSuperAdmin,
    isAdminFinanzas,
    isTallerOwner,
    isTallerUser,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
